// Category page data with SEO content, tools, FAQs, and related blog posts

export const categoryPages = {
  'text-tools': {
    id: 'text-tools',
    name: 'Text Tools',
    slug: 'text-tools',
    icon: 'type',
    seoTitle: 'Free Online Text Tools - Word Counter, Case Converter & More',
    seoDescription: 'Free online text tools for writers and content creators. Word counter, character counter, case converter, Lorem Ipsum generator, and diff checker. No signup required.',
    keywords: 'text tools, word counter, character counter, case converter, text converter, lorem ipsum, diff checker, text analyzer',
    heroTitle: 'Free Online Text Tools',
    heroSubtitle: 'Powerful text manipulation tools for writers, content creators, and anyone working with text',
    introduction: `Our collection of free text tools helps you analyze, transform, and generate text content efficiently. Whether you're counting words for a blog post, converting text case for code, or generating placeholder content, we've got you covered.

All our text tools work directly in your browser - no signup required, no data stored on servers. Your content stays private and secure.`,
    tools: [
      'text-counter',
      'case-converter',
      'lorem-ipsum-generator',
      'diff-checker',
      'markdown-to-html'
    ],
    relatedCategories: ['developer-tools', 'seo-tools'],
    faqs: [
      {
        q: 'Are these text tools free to use?',
        a: 'Yes, all our text tools are 100% free with no signup required. You can use them as many times as you need.'
      },
      {
        q: 'Is my text data secure?',
        a: 'Absolutely. All processing happens in your browser. Your text is never sent to our servers or stored anywhere.'
      },
      {
        q: 'Can I use these tools on mobile devices?',
        a: 'Yes, all our text tools are fully responsive and work great on phones, tablets, and desktops.'
      },
      {
        q: 'What\'s the maximum text length I can process?',
        a: 'There\'s no hard limit. Our tools can handle very long texts, though extremely large documents may take longer to process.'
      },
      {
        q: 'Do you support languages other than English?',
        a: 'Yes, our text tools work with any language and support Unicode characters, including emojis and special characters.'
      },
      {
        q: 'Can I use these tools for commercial projects?',
        a: 'Yes, you can use all our text tools for personal, educational, and commercial purposes without restrictions.'
      }
    ],
    relatedBlogPosts: [
      'best-free-online-tools-2026',
      'developer-utilities-guide',
      'free-tools-programmers'
    ],
    useCases: [
      { title: 'Content Writers', description: 'Count words and characters to meet article requirements' },
      { title: 'Students', description: 'Check essay length and format text properly' },
      { title: 'Developers', description: 'Convert variable names between different cases' },
      { title: 'Designers', description: 'Generate placeholder text for mockups' }
    ]
  },

  'developer-tools': {
    id: 'developer-tools',
    name: 'Developer Tools',
    slug: 'developer-tools',
    icon: 'code',
    seoTitle: 'Free Developer Tools Online - JSON, Regex, Hash & More',
    seoDescription: 'Essential free developer tools online. JSON formatter, regex tester, hash generator, timestamp converter, UUID generator. No installation required.',
    keywords: 'developer tools, json formatter, regex tester, hash generator, uuid generator, timestamp converter, code tools, programming utilities',
    heroTitle: 'Free Online Developer Tools',
    heroSubtitle: 'Essential utilities for developers, programmers, and software engineers',
    introduction: `Our developer tools suite provides essential utilities that every programmer needs. From formatting JSON responses to testing regular expressions, these tools streamline your development workflow.

Built by developers, for developers. All tools run client-side for maximum privacy and security - your code never leaves your browser.`,
    tools: [
      'json-formatter',
      'json-validator',
      'regex-tester',
      'timestamp-converter',
      'uuid-generator',
      'hash-generator',
      'diff-checker',
      'html-minifier',
      'css-minifier',
      'js-minifier'
    ],
    relatedCategories: ['converters', 'generators'],
    faqs: [
      {
        q: 'Do I need to install anything to use these tools?',
        a: 'No installation required. All tools work directly in your browser - just open the page and start using them.'
      },
      {
        q: 'Are these tools suitable for production use?',
        a: 'Yes, our tools produce production-quality output. However, for large-scale automation, consider integrating libraries into your build process.'
      },
      {
        q: 'Is my code secure when using these tools?',
        a: 'Absolutely. All processing happens locally in your browser. Your code is never sent to any server.'
      },
      {
        q: 'Which JSON features are supported?',
        a: 'Our JSON tools support standard JSON format with full Unicode support. JSON5 and JSONC (with comments) are not supported for validation.'
      },
      {
        q: 'What hash algorithms are available?',
        a: 'We support SHA-1, SHA-256, SHA-384, and SHA-512. SHA-256 is recommended for most use cases.'
      },
      {
        q: 'Can I use the regex tester for all programming languages?',
        a: 'Our regex tester uses JavaScript regex engine. Most patterns work across languages, but some advanced features may vary.'
      }
    ],
    relatedBlogPosts: [
      'how-to-format-json-complete-guide',
      'regex-tutorial-beginners',
      'developer-utilities-guide',
      'json-validator-guide'
    ],
    useCases: [
      { title: 'API Development', description: 'Format and validate JSON responses' },
      { title: 'Data Processing', description: 'Generate hashes and UUIDs for records' },
      { title: 'Debugging', description: 'Test regex patterns and compare code' },
      { title: 'Optimization', description: 'Minify HTML, CSS, and JavaScript' }
    ]
  },

  'seo-tools': {
    id: 'seo-tools',
    name: 'SEO Tools',
    slug: 'seo-tools',
    icon: 'search',
    seoTitle: 'Free SEO Tools Online - Meta Tags, Slug Generator & More',
    seoDescription: 'Free SEO tools to improve your website rankings. Meta tag generator, slug generator, URL encoder. Optimize your site for search engines.',
    keywords: 'seo tools, meta tag generator, slug generator, url encoder, seo optimization, website seo, search engine optimization',
    heroTitle: 'Free Online SEO Tools',
    heroSubtitle: 'Optimize your website for better search engine rankings',
    introduction: `Improve your website's search engine visibility with our free SEO tools. Generate optimized meta tags, create SEO-friendly URLs, and ensure your content is properly formatted for search engines.

These tools help you implement SEO best practices without expensive software subscriptions.`,
    tools: [
      'meta-tag-generator',
      'slug-generator',
      'url-encoder',
      'html-encoder',
      'text-counter'
    ],
    relatedCategories: ['text-tools', 'developer-tools'],
    faqs: [
      {
        q: 'How do meta tags affect SEO?',
        a: 'Meta tags help search engines understand your content. The title and description appear in search results and influence click-through rates.'
      },
      {
        q: 'What makes a good URL slug?',
        a: 'Good slugs are short, descriptive, use hyphens as separators, include relevant keywords, and avoid special characters.'
      },
      {
        q: 'Do I need to encode all URLs?',
        a: 'Only special characters in URLs need encoding. Our URL encoder handles spaces, symbols, and non-ASCII characters.'
      },
      {
        q: 'How long should my meta description be?',
        a: 'Keep meta descriptions between 150-160 characters. Longer descriptions may be truncated in search results.'
      },
      {
        q: 'Are Open Graph tags important for SEO?',
        a: 'While not direct ranking factors, Open Graph tags improve how your content appears when shared on social media, driving more traffic.'
      },
      {
        q: 'How often should I update meta tags?',
        a: 'Update meta tags when your content changes significantly or if you\'re not ranking well for target keywords.'
      }
    ],
    relatedBlogPosts: [
      'url-slug-generator-seo',
      'best-free-online-tools-2026',
      'minify-html-css-guide'
    ],
    useCases: [
      { title: 'Content Marketing', description: 'Optimize blog posts for search engines' },
      { title: 'E-commerce', description: 'Create SEO-friendly product URLs' },
      { title: 'Website Launch', description: 'Set up proper meta tags for all pages' },
      { title: 'Social Sharing', description: 'Configure Open Graph tags for social media' }
    ]
  },

  'generators': {
    id: 'generators',
    name: 'Generators',
    slug: 'generators',
    icon: 'sparkles',
    seoTitle: 'Free Online Generators - Password, UUID, Lorem Ipsum & More',
    seoDescription: 'Free online generator tools. Password generator, UUID generator, Lorem Ipsum generator, hash generator, placeholder images. Secure and instant.',
    keywords: 'generator tools, password generator, uuid generator, lorem ipsum generator, hash generator, placeholder generator, random generator',
    heroTitle: 'Free Online Generators',
    heroSubtitle: 'Generate passwords, UUIDs, placeholder content, and more instantly',
    introduction: `Our generator tools create secure passwords, unique identifiers, placeholder content, and more. All generators use cryptographically secure random functions where applicable.

Perfect for developers, designers, and anyone who needs to quickly generate content or identifiers.`,
    tools: [
      'password-generator',
      'uuid-generator',
      'lorem-ipsum-generator',
      'hash-generator',
      'placeholder-image',
      'meta-tag-generator',
      'slug-generator'
    ],
    relatedCategories: ['developer-tools', 'text-tools'],
    faqs: [
      {
        q: 'How secure is the password generator?',
        a: 'Our password generator uses the Web Crypto API for cryptographically secure random number generation. Passwords are generated locally and never transmitted.'
      },
      {
        q: 'What is a UUID and when should I use one?',
        a: 'UUID (Universally Unique Identifier) is a 128-bit identifier. Use UUIDs for database primary keys, session tokens, or any scenario requiring unique identifiers.'
      },
      {
        q: 'Is Lorem Ipsum real Latin?',
        a: 'Lorem Ipsum is based on classical Latin but scrambled. It\'s used as placeholder text because it has letter distribution similar to English.'
      },
      {
        q: 'Can I generate multiple items at once?',
        a: 'Yes, tools like UUID generator and Lorem Ipsum generator allow you to specify how many items to generate.'
      },
      {
        q: 'Are generated values truly random?',
        a: 'For security-sensitive tools like password generator, we use cryptographically secure random functions. Other tools use pseudo-random generation.'
      },
      {
        q: 'Can I customize what gets generated?',
        a: 'Yes, most generators offer customization options like length, character types, or content amount.'
      }
    ],
    relatedBlogPosts: [
      'secure-password-generator-guide',
      'what-is-base64-encoding',
      'developer-utilities-guide'
    ],
    useCases: [
      { title: 'Security', description: 'Generate strong, unique passwords' },
      { title: 'Development', description: 'Create UUIDs for database records' },
      { title: 'Design', description: 'Generate placeholder content and images' },
      { title: 'Testing', description: 'Create test data and identifiers' }
    ]
  },

  'converters': {
    id: 'converters',
    name: 'Converters',
    slug: 'converters',
    icon: 'repeat',
    seoTitle: 'Free Online Converters - Base64, URL, HTML, Timestamp & More',
    seoDescription: 'Free online converter tools. Base64 encoder/decoder, URL encoder, HTML encoder, timestamp converter, case converter, Markdown to HTML.',
    keywords: 'converter tools, base64 encoder, url encoder, html encoder, timestamp converter, case converter, markdown converter, encoding tools',
    heroTitle: 'Free Online Converters',
    heroSubtitle: 'Convert between different formats, encodings, and representations',
    introduction: `Our converter tools help you transform data between different formats. Encode and decode Base64, convert timestamps, change text case, and more.

All conversions happen instantly in your browser with full Unicode support.`,
    tools: [
      'base64-encoder',
      'url-encoder',
      'html-encoder',
      'timestamp-converter',
      'case-converter',
      'markdown-to-html',
      'number-formatter',
      'color-picker'
    ],
    relatedCategories: ['developer-tools', 'text-tools'],
    faqs: [
      {
        q: 'What is Base64 encoding used for?',
        a: 'Base64 converts binary data to text, commonly used for embedding images in HTML/CSS, email attachments, and API data transmission.'
      },
      {
        q: 'When should I URL encode text?',
        a: 'URL encode text when including special characters in URLs, particularly in query parameters or when the URL contains spaces or symbols.'
      },
      {
        q: 'What\'s the difference between URL and HTML encoding?',
        a: 'URL encoding (percent-encoding) is for URLs. HTML encoding converts characters to HTML entities for safe display in web pages.'
      },
      {
        q: 'Does the timestamp converter handle timezones?',
        a: 'Unix timestamps are UTC-based. The converter shows local time but stores/converts using UTC internally.'
      },
      {
        q: 'Can I convert between different case formats?',
        a: 'Yes, our case converter supports 9 formats: uppercase, lowercase, title case, sentence case, camelCase, PascalCase, snake_case, kebab-case, and CONSTANT_CASE.'
      },
      {
        q: 'Does Markdown to HTML support all Markdown features?',
        a: 'We support common Markdown features including headers, bold, italic, links, images, lists, code blocks, and blockquotes.'
      }
    ],
    relatedBlogPosts: [
      'what-is-base64-encoding',
      'how-to-format-json-complete-guide',
      'developer-utilities-guide'
    ],
    useCases: [
      { title: 'API Integration', description: 'Encode data for API requests' },
      { title: 'Web Development', description: 'Convert Markdown to HTML' },
      { title: 'Data Processing', description: 'Convert timestamps and formats' },
      { title: 'Content Creation', description: 'Transform text case and encoding' }
    ]
  },

  'calculator-tools': {
    id: 'calculator-tools',
    name: 'Calculator Tools',
    slug: 'calculator-tools',
    icon: 'calculator',
    seoTitle: 'Free Online Calculators - Financial, Crypto, Business & Tax Tools',
    seoDescription: 'Free online calculator tools. Financial calculators, crypto profit calculators, ROI calculators, tax estimators, loan calculators. No signup required.',
    keywords: 'calculator tools, financial calculator, crypto calculator, roi calculator, loan calculator, mortgage calculator, tax calculator, profit calculator, investment calculator',
    heroTitle: 'Free Online Calculator Tools',
    heroSubtitle: 'Financial, crypto, business, and tax calculators for smart decisions',
    introduction: `Our calculator tools help you make informed financial decisions. From basic percentage calculations to complex crypto profit analysis, mortgage payments, and investment projections.

All calculations happen instantly in your browser with no data stored on servers.`,
    tools: [
      // Financial Calculators
      'percentage-calculator',
      'roi-calculator',
      'profit-margin-calculator',
      'commission-calculator',
      'markup-calculator',
      'discount-calculator',
      'breakeven-calculator',
      // Loan & Mortgage
      'loan-calculator',
      'mortgage-calculator',
      'amortization-calculator',
      'credit-card-payoff-calculator',
      // Investment & Interest
      'investment-calculator',
      'savings-calculator',
      'simple-interest-calculator',
      'compound-interest-calculator',
      'cagr-calculator',
      'interest-calculator',
      'inflation-calculator',
      // Income & Tax
      'salary-calculator',
      'hourly-to-salary-calculator',
      'simple-tax-calculator',
      'net-income-calculator',
      // Marketing Calculators
      'conversion-rate-calculator',
      'cpa-calculator',
      'cpm-calculator',
      'cpc-calculator',
      'cac-calculator',
      'roas-calculator',
      // Crypto Calculators
      'crypto-profit-calculator',
      'crypto-tax-calculator',
      'crypto-roi-calculator',
      'crypto-gain-calculator',
      'crypto-investment-calculator',
      'bitcoin-profit-calculator',
      'bitcoin-roi-calculator',
      'ethereum-profit-calculator',
      'ethereum-roi-calculator',
      'solana-profit-calculator',
      'xrp-profit-calculator',
      'litecoin-profit-calculator',
      'dogecoin-profit-calculator',
      'cardano-profit-calculator',
      'polygon-profit-calculator'
    ],
    relatedCategories: ['developer-tools', 'seo-tools'],
    faqs: [
      {
        q: 'What calculators are available?',
        a: 'We offer 40+ calculators including financial (ROI, profit margin, loan), crypto (Bitcoin, Ethereum, tax), marketing (CPA, CPM, ROAS), and income calculators.'
      },
      {
        q: 'How accurate are the crypto profit calculators?',
        a: 'The math is 100% accurate. Your actual profits depend on real execution prices and exchange fees at the time of trade.'
      },
      {
        q: 'Can I use these for tax purposes?',
        a: 'Yes, our calculators provide estimates for planning. For official tax filing, consult a tax professional with the calculated figures.'
      },
      {
        q: 'Do you support different currencies?',
        a: 'Most calculators work with any currency. Enter amounts in your preferred currency and results will be in the same currency.'
      },
      {
        q: 'Is my financial data stored?',
        a: 'No, all calculations happen in your browser. We never store or transmit your financial data to any server.'
      },
      {
        q: 'What is ROI and how is it calculated?',
        a: 'ROI (Return on Investment) = ((Final Value - Initial Investment) / Initial Investment) × 100. It shows profit as a percentage of what you invested.'
      }
    ],
    relatedBlogPosts: [
      'crypto-profit-calculator-guide',
      'best-free-crypto-calculators-2026',
      'crypto-tax-calculator-guide'
    ],
    useCases: [
      { title: 'Investment Planning', description: 'Calculate potential returns and growth' },
      { title: 'Crypto Trading', description: 'Track profits, losses, and tax obligations' },
      { title: 'Loan Decisions', description: 'Compare mortgage and loan options' },
      { title: 'Business Finance', description: 'Calculate margins, markup, and breakeven' },
      { title: 'Marketing ROI', description: 'Measure campaign performance and costs' },
      { title: 'Tax Estimation', description: 'Estimate income tax and net earnings' }
    ]
  }
};

// Helper function to get category by slug
export const getCategoryBySlug = (slug) => {
  return categoryPages[slug] || null;
};

// Get all category slugs for routing
export const getAllCategorySlugs = () => {
  return Object.keys(categoryPages);
};

// Get category navigation items
export const getCategoryNavItems = () => {
  return Object.values(categoryPages).map(cat => ({
    slug: cat.slug,
    name: cat.name,
    icon: cat.icon
  }));
};
